import type { DiscountType } from './calculateLogic/CalculateDiscountTypes'
import {
  ContinuationDiscountApplyLimit,
  Discount,
  DiscountContinuation,
  UsedContinuationDiscountActivity
} from '../types/DiscountTypes'

/**
 * Discount types that do not use the top-level `value` field for pricing.
 * These are configured via nested structures (courseDiscounts, etc.).
 */
export const DISCOUNT_TYPES_WITHOUT_TOP_LEVEL_VALUE = [
  'none',
  'multi_course_matrix',
  'combo',
  'code',
  'advanced',
  'price_override'
] as const satisfies readonly DiscountType[]

export type DiscountTypeWithoutTopLevelValue = (typeof DISCOUNT_TYPES_WITHOUT_TOP_LEVEL_VALUE)[number]

const discountTypesWithoutTopLevelValueSet = new Set<DiscountType>(DISCOUNT_TYPES_WITHOUT_TOP_LEVEL_VALUE)

/**
 * Filters out deleted discounts from an array
 * Treats undefined isDeleted as false (active) for backward compatibility
 * @param discounts Array of discounts to filter
 * @returns Array of active (non-deleted) discounts
 */
export const getActiveDiscounts = (discounts: Discount[] | undefined | null): Discount[] => {
  if (!discounts) {
    return []
  }
  return discounts.filter((discount) => !discount.isDeleted)
}

/** True when the activity is linked to an active (not soft-deleted) discount of this type. */
export const activityHasActiveDiscountOfType = (
  activity: { discountIds?: string[] } | null | undefined,
  discounts: Discount[] | undefined | null,
  type: Discount['type']
): boolean => {
  const activeIds = new Set(
    getActiveDiscounts(discounts)
      .filter((discount) => discount.type === type)
      .map((discount) => discount.id)
  )
  if (activeIds.size === 0) return false
  return (activity?.discountIds || []).some((id) => activeIds.has(id))
}

/**
 * Registration / pricing gate for the Student checkbox.
 * True when ticking Student can change the price: an active student-type discount,
 * or an active students-only multi-course matrix linked to the activity.
 */
export const activityOffersStudentPricing = (
  activity: { discountIds?: string[] } | null | undefined,
  discounts: Discount[] | undefined | null
): boolean => {
  if (activityHasActiveDiscountOfType(activity, discounts, 'student')) return true
  const linkedIds = new Set(activity?.discountIds || [])
  return getActiveDiscounts(discounts).some(
    (discount) =>
      discount.type === 'multi_course_matrix' && discount.studentsOnly === true && linkedIds.has(discount.id)
  )
}

/** Whether an active discount must have a positive top-level value (percentage or amount). */
export const discountRequiresTopLevelValue = (discount: Discount): boolean => {
  if (discount.isDeleted) return false
  return !discountTypesWithoutTopLevelValueSet.has(discount.type)
}

/**
 * Normalizes qualifying periods for a continuation discount: prefers the new `qualifyingPeriodIds`
 * array, falling back to the legacy singular `qualifyingPeriodId` for docs written before this field
 * existed. Docs already in Firestore may lack `qualifyingPeriodIds` even though the type marks it
 * required, hence the defensive `?.` — never index the field directly outside this function.
 */
export const getContinuationQualifyingPeriodIds = (d: DiscountContinuation): string[] => {
  if (d.qualifyingPeriodIds?.length) return d.qualifyingPeriodIds
  if (d.qualifyingPeriodId) return [d.qualifyingPeriodId]
  return []
}

/** Defaults to Period so existing Firestore docs keep the original once-used behaviour. */
export const getContinuationApplyLimit = (d: DiscountContinuation): ContinuationDiscountApplyLimit =>
  d.applyLimit === ContinuationDiscountApplyLimit.Activity
    ? ContinuationDiscountApplyLimit.Activity
    : ContinuationDiscountApplyLimit.Period

export const collectUsedContinuationDiscountActivities = (
  enrollments: Array<{ priceDetails?: Array<{ activityId?: string; discountIds?: string[] }> }>,
  continuationDiscountIds: Set<string>
): UsedContinuationDiscountActivity[] => {
  const seen = new Set<string>()
  const result: UsedContinuationDiscountActivity[] = []
  for (const enrollment of enrollments) {
    for (const priceDetail of enrollment.priceDetails || []) {
      if (!priceDetail.activityId) continue
      for (const discountId of priceDetail.discountIds || []) {
        if (!continuationDiscountIds.has(discountId)) continue
        const key = `${discountId}:${priceDetail.activityId}`
        if (seen.has(key)) continue
        seen.add(key)
        result.push({ discountId, activityId: priceDetail.activityId })
      }
    }
  }
  return result
}

/**
 * Ensures the client's discounts[] master list has a live (non-deleted) entry of type 'code'.
 * Coupon codes are stored in a separate `discount_codes` subcollection, but the pricing engine
 * (`getActiveDiscounts` in `CalculateDiscountHelpers.ts`) only runs coupon-code logic if this
 * master entry exists and isn't soft-deleted — so a deleted/missing entry silently disables
 * every coupon code for the client even though each code doc still says `isActive: true`.
 * Restores a soft-deleted entry, creates one via `createDefault` if missing entirely, or is a
 * no-op if an active entry already exists.
 */
export const ensureActiveCodeDiscount = (
  discounts: Discount[] | undefined | null,
  createDefault: () => Discount
): { discounts: Discount[]; changed: boolean } => {
  const list = discounts ? [...discounts] : []
  const index = list.findIndex((d) => d.type === 'code')

  if (index === -1) {
    return { discounts: [...list, createDefault()], changed: true }
  }

  if (list[index].isDeleted) {
    list[index] = { ...list[index], isDeleted: false }
    return { discounts: list, changed: true }
  }

  return { discounts: list, changed: false }
}

export const getDiscountValueFieldErrorKey = (discountId: string): string => `discountValue_${discountId}`

export const getBulkTierValueFieldErrorKey = (discountId: string, tierIndex: number): string =>
  `discountBulkTier_${discountId}_${tierIndex}`

/** Validates a single percentage or amount value (amount is stored in öre). */
export const isNumericDiscountValueValid = (
  calculationType: Discount['calculationType'],
  value: number | undefined | null
): boolean => {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return false
  }

  if (calculationType === 'percentage') {
    return value >= 0 && value <= 100
  }

  // Amount discount (stored in öre) must be > 0
  return value > 0
}

/** Validates the top-level value for percentage and amount discounts. */
export const isDiscountValueValid = (discount: Discount): boolean => {
  if (!discountRequiresTopLevelValue(discount)) return true
  return isNumericDiscountValueValid(discount.calculationType, discount.value)
}

const getInvalidBulkTierFieldErrorKeys = (discount: Discount): string[] => {
  if (discount.isDeleted || discount.type !== 'multi_course_matrix') return []

  return (discount.courseDiscounts || [])
    .map((tier, index) =>
      isNumericDiscountValueValid(discount.calculationType, tier.discount)
        ? null
        : getBulkTierValueFieldErrorKey(discount.id, index)
    )
    .filter((key): key is string => key !== null)
}

/** Returns field-error keys for discounts with missing or invalid values. */
export const getInvalidDiscountValueFieldErrorKeys = (discounts: Discount[] | undefined | null): string[] => {
  if (!discounts) return []

  return discounts.flatMap((discount) => {
    const keys: string[] = []
    if (!isDiscountValueValid(discount)) {
      keys.push(getDiscountValueFieldErrorKey(discount.id))
    }
    keys.push(...getInvalidBulkTierFieldErrorKeys(discount))
    return keys
  })
}
